import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import {
  ICaseBattle,
  ICaseBattlePlayer,
} from '../interfaces/case-battles.interface';
import { groupBy } from '@core/utils/deprecated.utils';
import { sleep, sleepWithId } from '@core/utils/sleep.util';
import { fadeInAnimation } from '@core/utils/animations.util';
import { CaseBattlesService } from '../case-battles.service';
import { SoundsService } from '@core/services/sounds.service';

interface Option {
  name: string;
  is_winner: boolean;
  avatars: string[];
}

@Component({
  selector: 'app-cb-battle-tie',
  templateUrl: './cb-battle-tie.component.html',
  styleUrl: './cb-battle-tie.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [fadeInAnimation],
})
export class CbBattleTieComponent {
  private soundsService = inject(SoundsService);
  private caseBattlesService = inject(CaseBattlesService);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  battle = input.required<ICaseBattle>();

  battleSettings = computed(() => {
    return this.caseBattlesService.getBattleSettings(this.battle());
  });

  rollElement = viewChild.required<ElementRef<HTMLDivElement>>('roll');
  containerElement =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  options = computed(() => {
    const { type, participants, winners, tie_array, tiebreaker } =
      this.battle();

    const { ITEMS_QUANTITY, WIN_INDEX } = this.battleSettings();

    const isTeamMode = type === 'team';

    const teamOne = 1;
    const teamTwo = 2;

    const winnerSlots = winners?.map((winner) => winner.slot);
    const tieSlots = (
      ((tiebreaker?.length ?? 0) === 0 ? tie_array : tiebreaker) ?? []
    ).map((tie) => tie.slot);

    const tieParticipants = participants.filter((player: ICaseBattlePlayer) => {
      return isTeamMode ? true : tieSlots?.includes(player?.slot);
    });

    if (isTeamMode) {
      tieParticipants.forEach((p: any) => {
        p.team = p.slot === 1 || p.slot === 2 ? teamOne : teamTwo;
      });
    }

    const groupedParticipants = groupBy(
      tieParticipants,
      (player: ICaseBattlePlayer | any) =>
        String(isTeamMode ? player.team : player.slot)
    );

    const options: Option[] = Object.entries(groupedParticipants).map(
      ([key, team]): Option => {
        const avatars: string[] = [];
        const names: string[] = [];
        let isWinner = false;

        team.forEach((part) => {
          if (part.avatar) avatars.push(part.avatar);
          if (part.name) names.push(part.name);
          if (winnerSlots?.includes(part.slot)) isWinner = true;
        });

        return {
          name: names.join(' & '),
          is_winner: isWinner,
          avatars: avatars,
        };
      }
    );

    const avatars = [];
    const winningOption = options.find((slot) => slot.is_winner);

    for (let i = 0; i < ITEMS_QUANTITY; i++) {
      if (i === WIN_INDEX) {
        avatars[i] = winningOption;
      } else {
        avatars[i] = options[Math.floor(Math.random() * options.length)];
      }
    }

    return avatars;
  });

  private animationFrameId: number | null = null;

  showResults = signal(false);
  timeoutIds = signal<any[]>([]);

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
      this.timeoutIds().forEach((id) => clearTimeout(id));
    });
  }

  ngAfterViewInit() {
    runInInjectionContext(this.injector, async (): Promise<void> => {
      await this.roll();
    });
  }

  async roll() {
    const {
      ITEMS_QUANTITY,
      WIN_INDEX,
      TIE_ROLLING_TIMEOUT,
      TIE_BEZIER_FUNCTION,
      TIE_ROLLING_CLOSER_TIMEOUT,
    } = this.battleSettings();
    const size = 100 / ITEMS_QUANTITY;

    const middleLocation = -(size * WIN_INDEX) - size / 2;
    const transformX = -(size * WIN_INDEX) - size * (Math.random() * 0.6 + 0.2);
    await sleep(100);

    this.rollElement().nativeElement.style.transition = `transform ${TIE_ROLLING_TIMEOUT}ms ${TIE_BEZIER_FUNCTION}`;
    this.rollElement().nativeElement.style.transform = `translateX(${transformX}%)`;

    this.startAnimation(size);
    const { promise, timeoutId } = await sleepWithId(TIE_ROLLING_TIMEOUT);
    this.timeoutIds.update((ids) => [...ids, timeoutId]);
    await promise;
    this.stopAnimation();
    const { promise: promise2, timeoutId: timeoutId2 } = await sleepWithId(
      TIE_ROLLING_CLOSER_TIMEOUT
    );
    this.timeoutIds.update((ids) => [...ids, timeoutId2]);
    await promise2;

    const itemElement = this.rollElement().nativeElement.children[WIN_INDEX];

      this.soundsService.playSound('plinko-high', {
        maxVolume: 0.6,
        extension: 'mp3',
      });

    this.showResults.set(true);
    itemElement.classList.remove('active');
    itemElement.classList.add('win');
    this.rollElement().nativeElement.style.transition = `transform ${TIE_ROLLING_CLOSER_TIMEOUT}ms linear`;
    this.rollElement().nativeElement.style.transform = `translateX(${middleLocation}%)`;
  }

  private startAnimation(size: number) {
    const animate = () => {
      const containerRect =
        this.containerElement().nativeElement.getBoundingClientRect();
      const rollRect = this.rollElement().nativeElement.getBoundingClientRect();
      const rollCenter = containerRect.left + containerRect.width / 2;

      const transformPercentage =
        (Math.abs(rollRect.left - rollCenter) / rollRect.width) * 100;
      const currentRoll = Math.floor(transformPercentage / size);

      if (
        currentRoll >= 0 &&
        currentRoll < this.rollElement().nativeElement.children.length
      ) {
        this.updateActiveItem(currentRoll);
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private stopAnimation() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private updateActiveItem(index: number) {
    const rollElement = this.rollElement().nativeElement;
    const currentActive = rollElement.querySelector('.active');
    const newActive = rollElement.children[index];

    if (currentActive !== newActive) {
      if (currentActive) {
        currentActive.classList.remove('active');
      }
      if (newActive && !newActive.classList.contains('active')) {
        newActive.classList.add('active');
          this.soundsService.playSound('tick', {
            extension: 'mp3',
            maxVolume: 0.7,
          });
      }
    }
  }
}
